import React, { useState, useCallback, useEffect } from 'react';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { BaseLayout } from '@/components/layout';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Save, X, AlertTriangle, Loader2, Plus } from 'lucide-react';
import { ChecklistTemplate, ChecklistSection, ChecklistItem, FieldType } from '../types';
import {
  createEmptyTemplate,
  createEmptySection,
  createEmptyItem,
  moveArrayItem,
  validateTemplate,
} from '../utils';
import SectionComponent from './Section';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useChecklistWithDetails, useChecklistMutations } from '../hooks';
import { useAreaTagsByProject } from '@/features/area-tags/hooks/useAreaTags';
import { useToast } from '@/shared/hooks/use-toast';

const TemplateBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { templateId, projectId } = useParams<{ templateId?: string; projectId?: string }>();
  const [searchParams] = useSearchParams();
  const isInstance = searchParams.get('type') === 'instance';
  const areaQueryValue = searchParams.get('area');
  const parsedAreaFromQuery =
    areaQueryValue && !Number.isNaN(Number(areaQueryValue)) ? Number(areaQueryValue) : null;
  const { toast } = useToast();

  // State
  const [template, setTemplate] = useState<ChecklistTemplate>(createEmptyTemplate);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [newlyCreatedItemId, setNewlyCreatedItemId] = useState<string | null>(null);
  const [newlyCreatedSectionIds, setNewlyCreatedSectionIds] = useState<Set<string>>(new Set());
  const [newlyCreatedItemIds, setNewlyCreatedItemIds] = useState<Set<string>>(new Set());

  // Hooks - only fetch template details if we have a templateId
  const {
    data: existingTemplate,
    isLoading: isLoadingTemplate,
    error: loadError,
  } = useChecklistWithDetails(templateId || '', !!templateId);
  const { createTemplate, updateTemplate } = useChecklistMutations();
  const isEditingInstance = !!templateId && !!existingTemplate && !existingTemplate.is_template;
  const isInstanceBuilder = (!templateId && isInstance) || isEditingInstance;
  const { data: areaTags = [], isLoading: isLoadingAreaTags } = useAreaTagsByProject(projectId ?? '', {
    enabled: !!projectId && isInstanceBuilder,
  });

  // Initialize template when editing existing one
  useEffect(() => {
    if (templateId && existingTemplate) {
      setTemplate(existingTemplate);
      setIsDirty(false);
    } else if (!templateId) {
      // Creating new template - ensure project is set
      if (projectId) {
        setTemplate((prev) => ({
          ...prev,
          project: projectId,
          area: isInstance ? parsedAreaFromQuery : null,
        }));
      }
    }
  }, [templateId, existingTemplate, projectId, isInstance, parsedAreaFromQuery]);

  // Update project when property changes (for new templates)
  useEffect(() => {
    if (!templateId && projectId) {
      setTemplate((prev) => ({
        ...prev,
        project: projectId,
        area: isInstance ? prev.area ?? parsedAreaFromQuery : null,
      }));
    }
  }, [templateId, projectId, isInstance, parsedAreaFromQuery]);

  // Focus on newly created item
  useEffect(() => {
    if (newlyCreatedItemId) {
      const timer = setTimeout(() => {
        const itemElement = document.querySelector(`[data-item-id="${newlyCreatedItemId}"]`);
        if (itemElement) {
          itemElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
        setNewlyCreatedItemId(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [newlyCreatedItemId]);

  const updateTemplateState = useCallback((updates: Partial<ChecklistTemplate>) => {
    setTemplate((prev) => ({ ...prev, ...updates }));
    setIsDirty(true);
    setValidationErrors([]);
  }, []);

  const addSection = useCallback(
    (insertIndex?: number) => {
      const targetIndex = insertIndex !== undefined ? insertIndex : template.sections.length;
      const newSection = createEmptySection(template.id, targetIndex + 1);

      // Update order numbers for sections after the insertion point
      const updatedSections = [...template.sections];
      updatedSections.splice(targetIndex, 0, newSection);

      // Reorder all sections to have consecutive order numbers
      const reorderedSections = updatedSections.map((section, index) => ({
        ...section,
        order: index + 1,
      }));

      updateTemplateState({
        sections: reorderedSections,
      });

      // Mark section as newly created
      setNewlyCreatedSectionIds((prev) => new Set(prev).add(newSection.id));
    },
    [template.id, template.sections, updateTemplateState]
  );

  const addItem = useCallback(
    (sectionId: string, fieldType: FieldType = 'Free Text') => {
      const targetSection = template.sections.find((s) => s.id === sectionId);
      const itemOrder = targetSection ? targetSection.items.length + 1 : 1;
      const newItem = createEmptyItem(sectionId, fieldType, itemOrder);

      setTemplate((prev) => ({
        ...prev,
        sections: prev.sections.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                items: [...section.items, newItem],
                is_collapsed: false, // Expand the section when adding an item
              }
            : section
        ),
      }));
      setIsDirty(true);
      setNewlyCreatedItemId(newItem.id);

      // Mark item as newly created
      setNewlyCreatedItemIds((prev) => new Set(prev).add(newItem.id));
    },
    [template.sections]
  );

  const updateSection = useCallback((sectionId: string, updates: Partial<ChecklistSection>) => {
    setTemplate((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.id === sectionId ? { ...section, ...updates } : section
      ),
    }));
    setIsDirty(true);

    // Remove from newly created if user is typing in the title
    if (updates.title && updates.title.trim() !== '') {
      setNewlyCreatedSectionIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sectionId);
        return newSet;
      });
    }
  }, []);

  const updateItem = useCallback(
    (sectionId: string, itemId: string, updates: Partial<ChecklistItem>) => {
      setTemplate((prev) => ({
        ...prev,
        sections: prev.sections.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                items: section.items.map((item) =>
                  item.id === itemId ? { ...item, ...updates } : item
                ),
              }
            : section
        ),
      }));
      setIsDirty(true);

      // Remove from newly created if user is typing in the name
      if (updates.name && updates.name.trim() !== '') {
        setNewlyCreatedItemIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }
    },
    []
  );

  const deleteSection = useCallback(
    async (sectionId: string) => {
      // Only delete from backend if we're editing an existing template
      if (templateId && existingTemplate) {
        if (!projectId) {
          toast({
            title: 'Error',
            description: 'No project found. Please navigate here from a project page.',
            variant: 'destructive',
            className: 'bg-red-500 text-white',
            duration: 3000,
          });
          return;
        }

        try {
          const { deleteSection: deleteSectionAPI } = await import('../api/api');
          await deleteSectionAPI(sectionId, projectId);
        } catch (error) {
          console.error('Failed to delete section from backend:', error);
          toast({
            title: 'Error',
            description: 'Failed to delete section. Please try again.',
            variant: 'destructive',
            className: 'bg-red-500 text-white',
            duration: 3000,
          });
          return;
        }
      }

      // Update local state
      setTemplate((prev) => ({
        ...prev,
        sections: prev.sections.filter((section) => section.id !== sectionId),
      }));
      setIsDirty(true);
    },
    [toast, projectId, templateId, existingTemplate]
  );

  const deleteItem = useCallback(
    async (sectionId: string, itemId: string) => {
      // Only delete from backend if we're editing an existing template
      if (templateId && existingTemplate) {
        if (!projectId) {
          toast({
            title: 'Error',
            description: 'No project found. Please navigate here from a project page.',
            variant: 'destructive',
            className: 'bg-red-500 text-white',
            duration: 3000,
          });
          return;
        }

        try {
          const { deleteItem: deleteItemAPI } = await import('../api/api');
          await deleteItemAPI(itemId, projectId);
        } catch (error) {
          console.error('Failed to delete item from backend:', error);
          toast({
            title: 'Error',
            description: 'Failed to delete item. Please try again.',
            variant: 'destructive',
            className: 'bg-red-500 text-white',
            duration: 3000,
          });
          return;
        }
      }

      // Update local state
      setTemplate((prev) => ({
        ...prev,
        sections: prev.sections.map((section) =>
          section.id === sectionId
            ? { ...section, items: section.items.filter((item) => item.id !== itemId) }
            : section
        ),
      }));
      setIsDirty(true);
    },
    [toast, projectId, templateId, existingTemplate]
  );

  // Set up drag and drop monitoring
  useEffect(() => {
    return monitorForElements({
      onDrop({ source, location }) {
        const destination = location.current.dropTargets[0];
        if (!destination) return;

        const sourceData = source.data;
        const destData = destination.data;

        if (sourceData.type === 'section') {
          const sourceIndex = sourceData.index as number;
          const destIndex = destData.index as number;

          if (sourceIndex !== destIndex) {
            const newSections = moveArrayItem(template.sections, sourceIndex, destIndex);
            updateTemplateState({ sections: newSections });
          }
        } else if (sourceData.type === 'item') {
          const sourceSectionId = sourceData.sectionId as string;
          const sourceItemIndex = sourceData.index as number;
          const destSectionId = destData.sectionId as string;
          const destItemIndex = destData.index as number;

          const sourceSectionIndex = template.sections.findIndex((s) => s.id === sourceSectionId);
          const destSectionIndex = template.sections.findIndex((s) => s.id === destSectionId);

          if (sourceSectionIndex === -1 || destSectionIndex === -1) return;

          // Prevent moving between sections
          if (sourceSectionId !== destSectionId) {
            toast({
              title: 'Cannot Move Item Between Sections',
              description:
                'Items cannot be moved between sections. Please delete the item and recreate it in the desired section.',
              variant: 'destructive',
              className: 'bg-orange-500 text-white',
              duration: 4000,
            });
            return;
          }

          // Only proceed if actually moving to a different position
          if (sourceItemIndex !== destItemIndex) {
            const newSections = [...template.sections];
            const section = newSections[sourceSectionIndex];

            // Remove the item from its current position
            const [movedItem] = section.items.splice(sourceItemIndex, 1);

            // Insert it at the new position
            const insertIndex = destItemIndex > sourceItemIndex ? destItemIndex - 1 : destItemIndex;
            section.items.splice(insertIndex, 0, movedItem);

            updateTemplateState({ sections: newSections });
          }
        }
      },
    });
  }, [template.sections, updateTemplateState, toast]);

  const handleSave = async () => {
    const errors = validateTemplate(template);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (!projectId) {
      setValidationErrors(['No project found. Please navigate here from a project page.']);
      return;
    }

    try {
      if (templateId && existingTemplate) {
        // Update existing template
        const updatePayload = existingTemplate.is_template ? { ...template, area: null } : template;
        await updateTemplate.mutateAsync({
          id: templateId,
          updates: updatePayload,
        });
      } else {
        // Create new template
        const templateData = {
          title: template.title,
          description: template.description,
          is_template: !isInstance,
          area: isInstance ? template.area ?? null : null,
          template_source: null,
          sections: template.sections,
        };
        await createTemplate.mutateAsync(templateData);
      }

      setIsDirty(false);
      setValidationErrors([]);

      // Navigate back to dashboard after successful save
      navigate(getReturnPath());
    } catch (error) {
      console.error('Failed to save template:', error);
      setValidationErrors(['Failed to save punch list. Please try again.']);
    }
  };

  const getReturnPath = () => {
    if (!projectId) {
      return '/app/projects/all';
    }

    if (templateId && existingTemplate) {
      return existingTemplate.is_template
        ? `/app/property/${projectId}/punch-lists/templates`
        : `/app/property/${projectId}/punch-lists`;
    }

    return isInstance
      ? `/app/property/${projectId}/punch-lists`
      : `/app/property/${projectId}/punch-lists/templates`;
  };

  const handleCancel = () => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        navigate(getReturnPath());
      }
    } else {
      navigate(getReturnPath());
    }
  };

  // Loading state
  if (templateId && isLoadingTemplate) {
    return (
      <BaseLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">Loading template...</p>
          </div>
        </div>
      </BaseLayout>
    );
  }

  // Error state
  if (templateId && loadError) {
    return (
      <BaseLayout>
        <div className="p-4 md:p-6 lg:p-8">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Failed to load punch list. It may not exist or you may not have permission to
              view it.
            </AlertDescription>
          </Alert>
          <div className="mt-4">
            <Button onClick={() => navigate(getReturnPath())} variant="outline">
              Back
            </Button>
          </div>
        </div>
      </BaseLayout>
    );
  }

  const isSaving = createTemplate.isPending || updateTemplate.isPending;

  const AddSectionButton = ({ index, className = '' }: { index: number; className?: string }) => (
    <div className={`flex justify-center py-2 ${className}`}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => addSection(index)}
        className="text-blue-600 border-blue-300 hover:bg-blue-50 hover:border-blue-400"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Section
      </Button>
    </div>
  );

  return (
    <BaseLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-none lg:max-w-5xl xl:max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 xl:p-12">
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900">
                  {templateId ? 'Edit Punch List' : isInstance ? 'New Punch List' : 'New Punch List Template'}
                </h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
                  {templateId
                    ? 'Update your punch list'
                    : isInstance
                      ? 'Create a punch list for this project'
                      : 'Create a reusable punch list template'}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="flex-1 sm:flex-none"
                >
                  <X className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Cancel</span>
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !projectId}
                  className="bg-primary-600 hover:bg-primary-700 flex-1 sm:flex-none"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
                      <span className="hidden sm:inline">Saving...</span>
                      <span className="sm:hidden">Save</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">
                        {templateId ? 'Update Punch List' : 'Save Punch List'}
                      </span>
                      <span className="sm:hidden">Save</span>
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* No Property Selected Warning */}
            {!projectId && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  No project found. Please navigate here from a project's Punch Lists page.
                </AlertDescription>
              </Alert>
            )}

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <Alert variant="destructive" className="bg-red-100 border-red-500">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <AlertDescription>
                  <div className="space-y-1">
                    {validationErrors.map((error, index) => (
                      <div key={index}>{error}</div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Template Info */}
            <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 space-y-4">
              <div>
                <Label className="mb-2 text-gray-700">
                  Punch List Name
                </Label>
                <Input
                  value={template.title}
                  onChange={(e) => updateTemplateState({ title: e.target.value })}
                  placeholder="Enter template name..."
                  className="w-full sm:max-w-md text-base"
                />
              </div>
              <div>
                <Label className="mb-2 text-gray-700">
                  Description
                </Label>
                <Textarea
                  value={template.description}
                  onChange={(e) => updateTemplateState({ description: e.target.value })}
                  placeholder="Describe the purpose and scope of this punch list..."
                  className="w-full sm:max-w-2xl text-base"
                  rows={3}
                />
              </div>
              {isInstanceBuilder && (
                <div>
                  <Label className="mb-2 text-gray-700">Area Tag (optional)</Label>
                  <Select
                    value={template.area != null ? String(template.area) : 'general'}
                    onValueChange={(value) =>
                      updateTemplateState({
                        area: value === 'general' ? null : Number(value),
                      })
                    }
                  >
                    <SelectTrigger className="w-full sm:max-w-md">
                      <SelectValue placeholder="Select an area tag" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General (no area tag)</SelectItem>
                      {areaTags.map((areaTag) => (
                        <SelectItem key={areaTag.id} value={String(areaTag.id)}>
                          {areaTag.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isLoadingAreaTags && (
                    <p className="mt-1 text-xs text-muted-foreground">Loading available area tags...</p>
                  )}
                </div>
              )}
            </div>

            {/* Sections */}
            <div className="space-y-4 sm:space-y-6">
              {/* Add Section button at the top */}
              <AddSectionButton index={0} />

              {template.sections.map((section, index) => (
                <React.Fragment key={section.id}>
                  <SectionComponent
                    section={section}
                    index={index}
                    isNewlyCreated={newlyCreatedSectionIds.has(section.id)}
                    newlyCreatedItemIds={newlyCreatedItemIds}
                    onUpdate={(updates: Partial<ChecklistSection>) =>
                      updateSection(section.id, updates)
                    }
                    onDelete={() => deleteSection(section.id)}
                    onAddItem={(fieldType: FieldType) => addItem(section.id, fieldType)}
                    onUpdateItem={(itemId: string, updates: Partial<ChecklistItem>) =>
                      updateItem(section.id, itemId, updates)
                    }
                    onDeleteItem={(itemId: string) => deleteItem(section.id, itemId)}
                  />

                  {/* Add Section button between sections and after the last section */}
                  <AddSectionButton index={index + 1} />
                </React.Fragment>
              ))}

              {template.sections.length === 0 && (
                <div className="text-center py-8 sm:py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-500 mb-4 text-sm sm:text-base">
                    No sections yet. Add your first section to get started.
                  </p>
                  <Button onClick={() => addSection()} size="lg">
                    Add Section
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

export default TemplateBuilder;
